import React, { useState, useEffect, useCallback, useRef } from 'react';
import NewsletterComposer from './NewsletterComposer';

interface NewsletterViewProps {
  brand: string;
  // #285 B6: use the app-level toast channel instead of a local one.
  addToast: (type: 'success' | 'error' | 'running', title: string, detail?: string) => string;
  replaceToast: (id: string, type: 'success' | 'error' | 'running', title: string, detail?: string) => void;
}

interface EditionSummary {
  date: string;
  filename: string;
  totalSelected: number;
  sections: string[];
  foundersNote: string | null;
}

interface CuratedItem {
  title: string;
  url: string;
  source: string;
  summary: string;
  section: string;
  relevanceScore: number;
}

interface NewsletterSection {
  id: string;
  label: string;
  items: CuratedItem[];
}

// #294: newsletter quality eval result, attached to the edition + status by the
// generate route. Present only when the eval ran successfully.
interface NewsletterEval {
  score: number;
  band: string;
  hardFails: string[];
  slopFlags: string[];
}

interface NewsletterEdition {
  date: string;
  brand: string;
  foundersNote?: string;
  sections: NewsletterSection[];
  items: CuratedItem[];
  eval?: NewsletterEval;
  metadata: {
    totalCandidates: number;
    totalSelected: number;
    curatedAt: string;
    mode: 'llm' | 'mock';
  };
}

interface GenStatus {
  status: 'generating' | 'done' | 'error' | 'unknown';
  error?: string;
  totalSelected?: number;
  eval?: NewsletterEval;
}

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--success)';
  if (score >= 60) return 'var(--warning)';
  return 'var(--text-tertiary)';
}

// Faint tile behind the score badge. A token can't be alpha-suffixed like a hex
// (`var(--success)22` is invalid CSS), so use the paired -light tint tokens.
function scoreTint(score: number): string {
  if (score >= 80) return 'var(--success-light)';
  if (score >= 60) return 'var(--warning-light)';
  return 'var(--bg-surface)';
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NewsletterView({ brand, addToast, replaceToast }: NewsletterViewProps) {
  const [editions, setEditions] = useState<EditionSummary[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [edition, setEdition] = useState<NewsletterEdition | null>(null);
  const [loadingEditions, setLoadingEditions] = useState(true);
  const [loadingEdition, setLoadingEdition] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState<GenStatus | null>(null);
  // #294: optional editor's angle / instructions for the next generated edition.
  const [anglePrompt, setAnglePrompt] = useState('');
  const [showAngle, setShowAngle] = useState(false);

  // Founder's note editing
  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Send modal
  const [sendOpen, setSendOpen] = useState(false);
  const [buttondownKey, setButtondownKey] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ url?: string; error?: string } | null>(null);

  const genPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const genTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // #285 B6: route through the app-level toast channel (auto-dismiss). The
  // 'Generating…' progress toast becomes a running toast (genToastId) that
  // settleGen() morphs into success/error on completion — no stuck spinner.
  const genToastId = useRef<string | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' | 'info' = 'info') => { if (type !== 'info') addToast(type, msg); };
  const settleGen = useCallback((type: 'success' | 'error', msg: string) => {
    if (genToastId.current) { replaceToast(genToastId.current, type, msg); genToastId.current = null; }
    else addToast(type, msg);
  }, [addToast, replaceToast]);

  const fetchEditions = useCallback(() => {
    setLoadingEditions(true);
    fetch(`/api/${brand}/newsletter/editions`)
      .then((r) => r.json())
      .then((d) => { setEditions(d.editions || []); setLoadingEditions(false); })
      .catch(() => { setEditions([]); setLoadingEditions(false); });
  }, [brand]);

  useEffect(() => { fetchEditions(); }, [fetchEditions]);

  // Auto-select newest edition on load
  useEffect(() => {
    if (editions.length > 0 && !selectedDate) {
      setSelectedDate(editions[0].date);
    }
  }, [editions]);

  // Load edition when date changes
  useEffect(() => {
    if (!selectedDate) { setEdition(null); return; }
    setLoadingEdition(true);
    fetch(`/api/${brand}/newsletter/${selectedDate}`)
      .then((r) => r.json())
      .then((d) => {
        setEdition(d);
        setNoteValue(d.foundersNote ?? '');
        setLoadingEdition(false);
      })
      .catch(() => { setEdition(null); setLoadingEdition(false); });
  }, [brand, selectedDate]);

  // Poll generation status
  const stopGenPoll = () => {
    if (genPollRef.current) { clearInterval(genPollRef.current); genPollRef.current = null; }
    if (genTimeoutRef.current) { clearTimeout(genTimeoutRef.current); genTimeoutRef.current = null; }
  };

  const startGenPoll = useCallback((date: string) => {
    stopGenPoll();
    genPollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`/api/${brand}/newsletter/${date}/status`);
        const s: GenStatus = await r.json();
        setGenStatus(s);
        if (s.status === 'done') {
          stopGenPoll();
          setGenerating(false);
          settleGen('success', `Edition ${date} ready — ${s.totalSelected} items curated`);
          fetchEditions();
          setSelectedDate(date);
        } else if (s.status === 'error') {
          stopGenPoll();
          setGenerating(false);
          settleGen('error', `Generation failed: ${s.error}`);
        }
      } catch { /* network blip, keep polling */ }
    }, 2000);

    // Timeout after 90 seconds
    genTimeoutRef.current = setTimeout(() => {
      stopGenPoll();
      if (generating) {
        setGenerating(false);
        setGenStatus({ status: 'error', error: 'Timed out. Check server console.' });
        settleGen('error', 'Generation timed out. Check server logs.');
        fetchEditions();
      }
    }, 90_000);
  }, [brand, fetchEditions, settleGen, generating]);

  useEffect(() => () => stopGenPoll(), []);

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    setGenStatus({ status: 'generating' });
    try {
      const res = await fetch(`/api/${brand}/newsletter/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // #294: pass the optional editor's angle. Empty string omitted server-side.
        body: JSON.stringify(anglePrompt.trim() ? { prompt: anglePrompt.trim() } : {}),
      });
      const data = await res.json();
      if (!data.started) {
        setGenerating(false);
        // #212: surface the human-readable message (e.g. missing LLM key) rather than
        // the machine error code, so the user knows exactly what to configure.
        const msg = data.message || data.error || 'Failed to start generation';
        setGenStatus({ status: 'error', error: msg });
        showToast(msg, 'error');
      } else {
        genToastId.current = addToast('running', 'Generating edition…');
        startGenPoll(data.date);
      }
    } catch (err) {
      setGenerating(false);
      setGenStatus({ status: 'error', error: (err as Error).message });
    }
  };

  const handleSaveNote = async () => {
    if (!selectedDate || !edition) return;
    setSavingNote(true);
    try {
      await fetch(`/api/${brand}/newsletter/${selectedDate}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ foundersNote: noteValue }),
      });
      setEdition({ ...edition, foundersNote: noteValue });
      setEditingNote(false);
      showToast('Founder\'s note saved', 'success');
    } catch {
      showToast('Failed to save note', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  // Fallback copy for browsers/contexts where the async Clipboard API is
  // unavailable (non-secure origin, permission denied). Uses a hidden textarea
  // + execCommand('copy'); returns false if even that fails so the caller can
  // offer a last-resort "open in new tab" path.
  const legacyCopy = (text: string): boolean => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.pointerEvents = 'none';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopyHtml = async () => {
    if (!selectedDate) { showToast('Select an edition first', 'error'); return; }
    let html = '';
    try {
      const res = await fetch(`/api/${brand}/newsletter/${selectedDate}/html`);
      const bodyText = await res.text();
      if (!res.ok) {
        // A 404/500 returns a JSON error body (e.g. {"error":"Edition not found"}).
        // Surface it as an error toast; never copy the error body to the clipboard
        // under a success toast.
        let msg = `HTTP ${res.status}`;
        try { msg = (JSON.parse(bodyText) as { error?: string }).error || msg; } catch { /* not JSON — keep status */ }
        showToast(`Failed to fetch HTML: ${msg}`, 'error');
        return;
      }
      // Guard against a 200 that isn't actually HTML (e.g. a JSON error slipped
      // through with a 200) before treating the body as copyable HTML.
      const ct = res.headers.get('content-type') || '';
      if (ct && !ct.includes('html')) {
        showToast('Failed to fetch HTML: unexpected response type', 'error');
        return;
      }
      html = bodyText;
    } catch {
      showToast('Failed to fetch HTML', 'error');
      return;
    }
    // Prefer the async Clipboard API; on failure fall back to execCommand,
    // then to opening the HTML in a new tab so the user is never stuck.
    try {
      await navigator.clipboard.writeText(html);
      showToast('HTML copied to clipboard', 'success');
      return;
    } catch {
      if (legacyCopy(html)) {
        showToast('HTML copied to clipboard', 'success');
        return;
      }
      const w = window.open('', '_blank');
      if (w) {
        w.document.open();
        w.document.write('<pre style="white-space:pre-wrap;word-break:break-word">' +
          html.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</pre>');
        w.document.close();
        showToast('Clipboard blocked. HTML opened in a new tab to copy manually.', 'error');
      } else {
        showToast('Failed to copy HTML', 'error');
      }
    }
  };

  const handlePreviewHtml = () => {
    if (!selectedDate) { showToast('Select an edition first', 'error'); return; }
    window.open(`/api/${brand}/newsletter/${selectedDate}/html`, '_blank');
  };

  const handleSend = async () => {
    if (!selectedDate || !buttondownKey) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`/api/${brand}/newsletter/${selectedDate}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey: buttondownKey,
          subject: sendSubject || `ZopDev Weekly — ${selectedDate}`,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSendResult({ url: data.url });
        showToast('Draft created in Buttondown', 'success');
      } else {
        setSendResult({ error: data.error });
      }
    } catch (err) {
      setSendResult({ error: (err as Error).message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="view-page" style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0, padding: 0 }}>


      {/* Send Modal */}
      {sendOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => { if (!sending) setSendOpen(false); }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 0,
            padding: '24px 28px', width: 440, boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Send to Buttondown
            </h3>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--text-secondary)' }}>
              Creates a draft in your Buttondown account. Review and send from there.
            </p>
            <label htmlFor="newsletter-send-subject" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>
              SUBJECT LINE
            </label>
            <input
              id="newsletter-send-subject"
              value={sendSubject}
              onChange={(e) => setSendSubject(e.target.value)}
              placeholder={`ZopDev Weekly — ${selectedDate}`}
              style={{
                width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border)',
                borderRadius: 0, padding: '8px 10px', fontSize: 13, color: 'var(--text-primary)',
                marginBottom: 14, boxSizing: 'border-box',
              }}
            />
            <label htmlFor="newsletter-send-buttondown-key" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>
              BUTTONDOWN API KEY
            </label>
            <input
              id="newsletter-send-buttondown-key"
              type="password"
              value={buttondownKey}
              onChange={(e) => setButtondownKey(e.target.value)}
              placeholder="sk-..."
              style={{
                width: '100%', background: 'var(--surface-raised)', border: '1px solid var(--border)',
                borderRadius: 0, padding: '8px 10px', fontSize: 13, color: 'var(--text-primary)',
                marginBottom: 18, boxSizing: 'border-box',
              }}
            />
            {sendResult?.error && (
              <div style={{ padding: '8px 12px', background: 'var(--danger-light)', border: '1px solid var(--danger)', borderRadius: 0, fontSize: 13, color: 'var(--danger)', marginBottom: 14 }}>
                {sendResult.error}
              </div>
            )}
            {sendResult?.url && (
              <div
                className="alert alert--success"
                style={{ marginBottom: 14, padding: '10px 14px', display: 'block' }}
              >
                <div className="alert__title">Draft created</div>
                <div className="alert__desc">
                  <a
                    href={sendResult.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--ink)', textDecoration: 'underline' }}
                  >
                    {sendResult.url}
                  </a>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSendOpen(false)} disabled={sending} style={{ fontSize: 13 }}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSend}
                disabled={sending || !buttondownKey}
                style={{ fontSize: 13, minWidth: 110 }}
              >
                {sending ? 'Sending...' : 'Create Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 24px 14px', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            Newsletter
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Curated editions from industry feeds and your own content
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => setShowAngle((v) => !v)}
            disabled={generating}
            className="btn btn-secondary"
            aria-expanded={showAngle}
            style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
            title="Add an optional editorial angle for this edition"
          >
            Angle{anglePrompt.trim() ? ' •' : ''}
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 160 }}
          >
            {generating ? (
              <>
                <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: 14 }}>&#x21BB;</span>
                Generating...
              </>
            ) : (
              <>
                <span style={{ fontSize: 15 }}>&#x2B;</span>
                Generate Edition
              </>
            )}
          </button>
        </div>
      </div>

      {/* #294: optional editor's angle for the next generated edition */}
      {showAngle && (
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg-surface)' }}>
          <label htmlFor="newsletter-angle" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Angle / instructions (optional)
          </label>
          <textarea
            id="newsletter-angle"
            value={anglePrompt}
            onChange={(e) => setAnglePrompt(e.target.value)}
            rows={2}
            disabled={generating}
            placeholder="e.g. Focus on Kubernetes cost stories and skip vendor funding news."
            style={{
              width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 0, padding: '8px 10px', fontSize: 13, color: 'var(--text-primary)',
              lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-tertiary)' }}>
            Applied to the next generated edition. Leave empty for default curation.
          </p>
        </div>
      )}

      {/* Generation status bar */}
      {generating && genStatus && (
        <div style={{
          margin: '0', padding: '8px 24px', background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--accent)', fontSize: 13, color: 'var(--accent)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>&#x21BB;</span>
          Fetching RSS feeds and curating items... this takes 30-60 seconds
        </div>
      )}
      {!generating && genStatus?.status === 'error' && (
        <div style={{ margin: '0', padding: '8px 24px', background: 'var(--danger-light)', borderBottom: '1px solid var(--danger)', fontSize: 13, color: 'var(--danger)' }}>
          Generation failed: {genStatus.error}
        </div>
      )}

      {/* Body: sidebar + preview */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Editions sidebar */}
        <div style={{
          width: 220, flexShrink: 0, borderRight: '1px solid var(--border)',
          overflowY: 'auto', padding: '12px 0',
        }}>
          {loadingEditions ? (
            <div style={{ padding: '16px 20px', color: 'var(--text-tertiary)', fontSize: 13 }}>Loading editions…</div>
          ) : editions.length === 0 ? (
            <div style={{ padding: '16px 20px', color: 'var(--text-tertiary)', fontSize: 13 }}>
              No editions yet.<br />
              <span style={{ color: 'var(--text-quaternary)', fontSize: 12 }}>Click "Generate Edition" to create the first one.</span>
            </div>
          ) : (
            editions.map((ed) => (
              <button
                key={ed.date}
                onClick={() => setSelectedDate(ed.date)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 16px', border: 'none', cursor: 'pointer',
                  background: selectedDate === ed.date ? 'var(--surface-hover)' : 'transparent',
                  borderLeft: selectedDate === ed.date ? '3px solid var(--accent)' : '3px solid transparent',
                  transition: 'background 0.15s',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{ed.date}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {ed.totalSelected} items &middot; {ed.sections.length} sections
                </div>
              </button>
            ))
          )}
        </div>

        {/* Preview panel */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          <NewsletterComposer brand={brand} />
          {loadingEdition ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Loading edition...</div>
          ) : !edition ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 13, paddingTop: 40, textAlign: 'center' }}>
              {editions.length === 0 ? 'Generate your first newsletter edition to get started.' : 'Select an edition from the left.'}
            </div>
          ) : (
            <div>
              {/* Edition header */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {formatDate(edition.date)}
                  </h3>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 0,
                    background: edition.metadata.mode === 'llm' ? 'var(--bg-surface)' : 'var(--bg-hover)',
                    color: edition.metadata.mode === 'llm' ? 'var(--accent)' : 'var(--text-tertiary)',
                    fontWeight: 600, letterSpacing: 0.3,
                  }}>
                    {edition.metadata.mode.toUpperCase()}
                  </span>
                  {/* #294: newsletter quality eval score chip (shown when eval ran) */}
                  {edition.eval && (
                    <span
                      title={
                        (edition.eval.hardFails.length ? `Hard fails: ${edition.eval.hardFails.join('; ')}\n` : '') +
                        (edition.eval.slopFlags.length ? `Slop: ${edition.eval.slopFlags.join('; ')}` : 'No slop flags')
                      }
                      style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 0, fontWeight: 700,
                        letterSpacing: 0.3,
                        background: scoreTint(edition.eval.score),
                        color: scoreColor(edition.eval.score),
                        border: `1px solid ${scoreColor(edition.eval.score)}`,
                      }}
                    >
                      QUALITY {edition.eval.score} · {edition.eval.band.toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                  {edition.metadata.totalSelected} items selected from {edition.metadata.totalCandidates} candidates &middot; curated {timeAgo(edition.metadata.curatedAt)}
                </div>
              </div>

              {/* Founder's note */}
              <div style={{
                background: 'var(--surface-raised)', border: '1px solid var(--border)',
                borderRadius: 0, padding: '14px 18px', marginBottom: 24,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Founder's Note
                  </div>
                  {!editingNote && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => { setNoteValue(edition.foundersNote ?? ''); setEditingNote(true); }}
                      style={{ fontSize: 11, padding: '3px 10px' }}
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingNote ? (
                  <>
                    <textarea
                      value={noteValue}
                      onChange={(e) => setNoteValue(e.target.value)}
                      rows={4}
                      style={{
                        width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 0, padding: '8px 10px', fontSize: 13.5, color: 'var(--text-primary)',
                        lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary" onClick={() => setEditingNote(false)} style={{ fontSize: 12 }}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleSaveNote} disabled={savingNote} style={{ fontSize: 12 }}>
                        {savingNote ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <p style={{ margin: 0, fontSize: 13.5, color: edition.foundersNote ? 'var(--text-secondary)' : 'var(--text-tertiary)', lineHeight: 1.6, fontStyle: edition.foundersNote ? 'normal' : 'italic' }}>
                    {edition.foundersNote || 'No founder\'s note. Click Edit to add one.'}
                  </p>
                )}
              </div>

              {/* Sections */}
              {edition.sections.length === 0 ? (
                <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No items were curated for this edition.</div>
              ) : (
                edition.sections.map((section) => (
                  <div key={section.id} style={{ marginBottom: 28 }}>
                    <h4 style={{
                      margin: '0 0 12px', fontSize: 13, fontWeight: 700,
                      color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.8,
                      paddingBottom: 6, borderBottom: '1px solid var(--border)',
                    }}>
                      {section.label}
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: 'var(--text-tertiary)', textTransform: 'none', letterSpacing: 0 }}>
                        {section.items.length} {section.items.length === 1 ? 'item' : 'items'}
                      </span>
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {section.items.map((item, i) => (
                        <div key={i} style={{
                          background: 'var(--surface-raised)', border: '1px solid var(--border)',
                          borderRadius: 0, padding: '12px 16px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none', flex: 1, lineHeight: 1.3 }}
                            >
                              {item.title}
                            </a>
                            <span style={{
                              flexShrink: 0, fontSize: 11, fontWeight: 700,
                              padding: '2px 7px', borderRadius: 0,
                              background: scoreTint(item.relevanceScore),
                              color: scoreColor(item.relevanceScore),
                              border: `1px solid ${scoreColor(item.relevanceScore)}`,
                            }}>
                              {item.relevanceScore}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                            {item.source}
                          </div>
                          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            {item.summary}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}

              {/* Action bar */}
              <div style={{ marginTop: 8, paddingTop: 20, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handlePreviewHtml}
                  style={{ fontSize: 13 }}
                  title="Open rendered email HTML in a new tab"
                >
                  Preview Email
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleCopyHtml}
                  style={{ fontSize: 13 }}
                  title="Copy rendered HTML to clipboard"
                >
                  Copy HTML
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => { setSendResult(null); setSendSubject(''); setSendOpen(true); }}
                  style={{ fontSize: 13 }}
                >
                  Send via Buttondown
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
